import Keycloak from 'keycloak-js';
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';

import type { User } from '@/types';
import { setAuthTokenProvider } from '@/lib/auth-token';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  token: string | null;
  tenantId: string | null;
  roles: string[];
  permissions: string[];
  login: () => Promise<void>;
  logout: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const keycloakConfig = {
  url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
  realm: import.meta.env.VITE_KEYCLOAK_REALM || 'egpa',
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'egpa-frontend',
};

const keycloak = new Keycloak(keycloakConfig);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [permissions, setPermissions] = useState<string[]>([]);

  const loadPermissionsFromApi = useCallback(async (accessToken: string) => {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    try {
      const response = await fetch(`${apiUrl}/api/v1/auth/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!response.ok) {
        return;
      }
      const data = await response.json();
      if (Array.isArray(data?.permissions)) {
        setPermissions(data.permissions);
      }
      if (Array.isArray(data?.roles)) {
        setRoles(data.roles);
      }
    } catch (error) {
      console.warn('Could not load permissions from backend:', error);
    }
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<string | null> => {
    if (!keycloak.authenticated) {
      return localStorage.getItem('token');
    }

    try {
      await keycloak.updateToken(30);
      const accessToken = keycloak.token || null;
      if (accessToken) {
        localStorage.setItem('token', accessToken);
        setToken(accessToken);
      }
      return accessToken;
    } catch (error) {
      console.warn('Token refresh failed:', error);
      localStorage.removeItem('token');
      setToken(null);
      setIsAuthenticated(false);
      return null;
    }
  }, []);

  useEffect(() => {
    setAuthTokenProvider(refreshAccessToken);
  }, [refreshAccessToken]);

  useEffect(() => {
    let refreshInterval: number | undefined;

    const initKeycloak = async () => {
      try {
        const authenticated = await keycloak.init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri:
            window.location.origin + '/silent-check-sso.html',
          pkceMethod: 'S256',
        });

        setIsAuthenticated(authenticated);

        if (authenticated) {
          const accessToken = keycloak.token || null;
          setToken(accessToken);
          
          // Store token in localStorage for API calls
          if (accessToken) {
            localStorage.setItem('token', accessToken);
            await loadPermissionsFromApi(accessToken);
          }

          // Extract user info from token
          const tokenParsed = keycloak.tokenParsed;
          if (tokenParsed) {
            setUser({
              id: tokenParsed.sub || '',
              keycloakId: tokenParsed.sub || '',
              tenantId: tokenParsed.tenant_id || '',
              username: tokenParsed.preferred_username || '',
              email: tokenParsed.email,
              displayName: tokenParsed.name,
              roles: tokenParsed.realm_access?.roles || [],
              createdAt: '',
              updatedAt: '',
            });
            setTenantId(tokenParsed.tenant_id || null);
            setRoles(tokenParsed.realm_access?.roles || []);
          }

          // Setup token refresh
          keycloak.onTokenExpired = () => {
            refreshAccessToken()
              .then((refreshedToken) => {
                if (refreshedToken) {
                  loadPermissionsFromApi(refreshedToken).catch(() => {});
                }
              })
              .catch(() => {});
          };

          refreshInterval = window.setInterval(() => {
            if (keycloak.authenticated) {
              refreshAccessToken().catch(() => {});
            }
          }, 60_000);
        }
      } catch (error) {
        console.error('Keycloak init error:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initKeycloak();

    return () => {
      if (refreshInterval) {
        window.clearInterval(refreshInterval);
      }
    };
  }, [loadPermissionsFromApi, refreshAccessToken]);

  const login = useCallback(async () => {
    await keycloak.login();
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem('token');
    await keycloak.logout({ redirectUri: window.location.origin + '/login' });
    setIsAuthenticated(false);
    setUser(null);
    setToken(null);
    setTenantId(null);
    setRoles([]);
    setPermissions([]);
  }, []);

  const hasRole = useCallback(
    (role: string) => {
      return roles.includes(role);
    },
    [roles]
  );

  const hasAnyRole = useCallback(
    (checkRoles: string[]) => {
      return checkRoles.some((role) => roles.includes(role));
    },
    [roles]
  );

  const hasPermission = useCallback(
    (permission: string) => {
      return permissions.includes(permission);
    },
    [permissions]
  );

  const hasAnyPermission = useCallback(
    (requiredPermissions: string[]) => {
      return requiredPermissions.some((permission) => permissions.includes(permission));
    },
    [permissions]
  );

  const hasAllPermissions = useCallback(
    (requiredPermissions: string[]) => {
      return requiredPermissions.every((permission) => permissions.includes(permission));
    },
    [permissions]
  );

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        token,
        tenantId,
        roles,
        permissions,
        login,
        logout,
        hasRole,
        hasAnyRole,
        hasPermission,
        hasAnyPermission,
        hasAllPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
